
import { defineComponent, PropType } from "vue";
import DraftTreeView from "./DraftTreeView.vue";
import { buildDraftForest, DraftForest, packageLabel } from "./draftTree";
import { buildDraftExportHtml, draftExportBaseName } from "./draftTreeExport";
import { MigrationDraft, MigrationDraftNode } from "./types";

type FilterKey = "all" | "anchors" | "genesis" | "vip" | "lite";
type ViewKey = "binary" | "unilevel" | "table";

/**
 * LA REVISIÓN DEL DRAFT, CON TRES LENTES SOBRE EL MISMO DATO.
 *
 * Aquí es donde se caza un ancla mal sembrada ANTES de que se escriba nada, y
 * hacen falta las tres:
 *
 *  · Los DOS ÁRBOLES dibujados —el binario y el de patrocinio— porque «quién
 *    quedó debajo de quién» es una FORMA, y una columna de padres no la enseña.
 *    Son dos árboles distintos a propósito y por eso van en pestañas separadas:
 *    la divergencia entre ellos (a quién te patrocina no es de quien cuelgas) es
 *    justo lo que hay que poder ver.
 *  · La TABLA, que se queda, porque para buscar un nombre concreto y comparar
 *    campo a campo una lista se lee mejor que un dibujo.
 *
 * (Validar el árbol DESPUÉS de aplicarlo es otro trabajo y ya tiene su
 * herramienta: la Vista de Árbol de siempre, con `useNewTree`. Ésa lee de
 * Firestore lo que YA está escrito; ésta pinta lo que todavía NO existe, que es
 * la única razón por la que hay un componente nuevo y no una reutilización.)
 */
export default defineComponent({
  name: "DraftPreviewTable",
  components: { DraftTreeView },
  props: {
    draft: { type: Object as PropType<MigrationDraft>, required: true },
  },
  data() {
    return {
      // Se abre por el ÁRBOL BINARIO: es la colocación permanente, la que tiene
      // consecuencias de dinero y la que no se deshace.
      view: "binary" as ViewKey,
      tabs: [
        {
          key: "binary" as ViewKey,
          label: "Árbol binario",
          icon: "fa-sitemap",
        },
        {
          key: "unilevel" as ViewKey,
          label: "Árbol de patrocinio (unilevel)",
          icon: "fa-users",
        },
        { key: "table" as ViewKey, label: "Tabla", icon: "fa-table" },
      ],
      filter: "all" as FilterKey,
      search: "",
      exportError: "",
      filters: [
        { key: "all" as FilterKey, label: "Todos" },
        { key: "anchors" as FilterKey, label: "Anclas" },
        { key: "genesis" as FilterKey, label: "Génesis" },
        { key: "vip" as FilterKey, label: "VIP" },
        { key: "lite" as FilterKey, label: "Lite" },
      ],
    };
  },
  computed: {
    // LOS BOSQUES SE ARMAN AQUÍ, una sola vez, y bajan a las dos vistas. El
    // componente del árbol ya no sabe construirlos: los tres árboles de la
    // consola salen de drafts con formas distintas (éste y el del CMMI), así
    // que quien tiene el draft es quien lo convierte.
    binaryForest(): DraftForest {
      return buildDraftForest(this.draft, "binary");
    },
    unilevelForest(): DraftForest {
      return buildDraftForest(this.draft, "unilevel");
    },
    rows(): MigrationDraftNode[] {
      const term = this.search.trim().toLowerCase();
      return this.matching(this.filter).filter((n) => {
        if (!term) return true;
        return (
          n.username.includes(term) ||
          (n.binaryParentPK ?? "").includes(term) ||
          n.unilevelParentPK.includes(term) ||
          n.enroller.includes(term)
        );
      });
    },
  },
  methods: {
    matching(key: FilterKey): MigrationDraftNode[] {
      const nodes = this.draft.nodes ?? [];
      switch (key) {
        case "anchors":
          return nodes.filter((n) => n.isAnchor);
        case "genesis":
          return nodes.filter((n) => n.isGenesis);
        case "vip":
          return nodes.filter((n) => n.tier === "vip");
        case "lite":
          return nodes.filter((n) => n.tier === "lite");
        default:
          return nodes;
      }
    },
    countOf(key: FilterKey): number {
      return this.matching(key).length;
    },
    /**
     * El color del badge sigue saliendo del TIER, no del nombre comercial: es
     * el mismo dato con otra etiqueta, y el motor no se toca. Lo único que
     * cambió es la palabra que se pinta.
     */
    tierClass(tier: string): string {
      if (tier === "vip") return "bg-dark";
      if (tier === "lite") return "bg-secondary";
      return "bg-primary";
    },
    packageLabel,
    /**
     * EL FICHERO PARA LOS QUE NO ENTRAN AQUÍ.
     *
     * Se construye desde el draft que ya está en memoria —no se vuelve a llamar
     * al backend— y desde el BOSQUE, no desde el DOM: así sale expandido aunque
     * en pantalla haya ramas plegadas, y no arrastra ni una clase de la
     * aplicación. Es síncrono y sin librerías: una cadena, un Blob y un
     * `<a download>`.
     */
    exportHtml(): void {
      this.exportError = "";
      try {
        const now = Date.now();
        const html = buildDraftExportHtml(this.draft, {
          runLabel: this.draft.generatedFor || "",
          generatedAt: now,
          migrationDate: this.draft.migrationDate,
          root: this.draft.root,
        });
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${draftExportBaseName(
          this.draft.generatedFor,
          now
        )}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Sin esto el Blob se queda en memoria hasta que se recarga la página, y
        // esta consola se tiene abierta horas durante la ventana.
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (e) {
        this.exportError = (e as Error)?.message || String(e);
      }
    },
  },
});
